import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useToast } from '../../contexts/ToastContext';

function formatDate(value) {
  return value ? dayjs(value).format('YYYY-MM-DD') : '—';
}

function personLabel(user) {
  if (!user) return '—';
  if (typeof user === 'string') return user;
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';
}

function EmptyRow({ colSpan, label }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        {label}
      </TableCell>
    </TableRow>
  );
}

function Section({ title, count, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        <Chip size="small" label={count} />
      </Stack>
      {children}
    </Paper>
  );
}

/**
 * Today / This week — assigned tasks, leaves, latecoming, roster, training, OT.
 * Available to all Staff details users; HR/managers also see people under them.
 */
function TodayThisWeekPanel({ canViewTeam = false }) {
  const { showToast } = useToast();
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/hrms/digest', { params: { period } });
      setDigest(res.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load Today / This week', 'error');
      setDigest(null);
    } finally {
      setLoading(false);
    }
  }, [period, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !digest) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const roster = digest?.roster || [];
  const leaves = digest?.leaves || [];
  const late = digest?.late || [];
  const attendance = digest?.attendance || [];
  const tasks = digest?.tasks || [];
  const trainings = digest?.trainings || [];
  const overtime = digest?.overtime || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <Tabs
          value={period}
          onChange={(_, v) => setPeriod(v)}
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
        >
          <Tab value="today" label="Today" />
          <Tab value="week" label="This week" />
        </Tabs>
        {digest && (
          <Typography variant="body2" color="text.secondary">
            {digest.label}
            {digest.from && digest.to
              ? ` · ${formatDate(digest.from)}${digest.period === 'week' ? ` – ${formatDate(digest.to)}` : ''}`
              : ''}
            {canViewTeam && digest.scopeCount != null
              ? ` · ${digest.scopeCount} people in scope`
              : ''}
          </Typography>
        )}
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        {canViewTeam
          ? 'Showing your activity plus everyone under you for this period.'
          : 'Showing your assigned tasks, leaves, attendance, and related items for this period.'}
      </Alert>

      {loading && (
        <Box display="flex" justifyContent="center" mb={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Section title="Late / absent" count={late.length}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {late.length === 0 && <EmptyRow colSpan={4} label="No late or absent marks" />}
            {late.map((a) => (
              <TableRow key={a._id}>
                <TableCell>{formatDate(a.date)}</TableCell>
                <TableCell>{personLabel(a.userId)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={a.status === 'ABSENT' ? 'error' : 'warning'}
                    label={a.status}
                  />
                </TableCell>
                <TableCell>{a.remarks || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="Leaves & availability" count={leaves.length}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaves.length === 0 && <EmptyRow colSpan={6} label="No leave or availability in this period" />}
            {leaves.map((row) => (
              <TableRow key={row._id}>
                <TableCell>{personLabel(row.userId)}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{formatDate(row.startDate)}</TableCell>
                <TableCell>{formatDate(row.endDate)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.status === 'APPROVED' ? 'success' : 'default'}
                    label={row.status}
                  />
                </TableCell>
                <TableCell>{row.reason || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="Assigned tasks" count={tasks.length}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Group</TableCell>
              <TableCell>Assignees</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.length === 0 && <EmptyRow colSpan={5} label="No group tasks due or open in this period" />}
            {tasks.map((t) => (
              <TableRow key={t._id}>
                <TableCell>{t.title || '—'}</TableCell>
                <TableCell>{t.groupId?.name || '—'}</TableCell>
                <TableCell>
                  {(t.assigneeIds || []).map((u) => personLabel(u)).join(', ') || '—'}
                </TableCell>
                <TableCell>{formatDate(t.dueDate)}</TableCell>
                <TableCell>
                  <Chip size="small" label={t.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="Roster" count={roster.length}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Shift</TableCell>
              <TableCell>Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roster.length === 0 && <EmptyRow colSpan={4} label="No shifts scheduled" />}
            {roster.map((r) => (
              <TableRow key={r._id}>
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell>{personLabel(r.userId)}</TableCell>
                <TableCell>{r.shiftId?.name || '—'}</TableCell>
                <TableCell>
                  {r.shiftId ? `${r.shiftId.startTime}–${r.shiftId.endTime}` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="Attendance" count={attendance.length}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attendance.length === 0 && <EmptyRow colSpan={4} label="No attendance marked" />}
            {attendance.map((a) => (
              <TableRow key={a._id}>
                <TableCell>{formatDate(a.date)}</TableCell>
                <TableCell>{personLabel(a.userId)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={
                      a.status === 'LATE' || a.status === 'ABSENT'
                        ? a.status === 'ABSENT' ? 'error' : 'warning'
                        : 'default'
                    }
                    label={a.status}
                  />
                </TableCell>
                <TableCell>{a.remarks || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="Training" count={trainings.length}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trainings.length === 0 && <EmptyRow colSpan={4} label="No trainings due or in progress" />}
            {trainings.map((t) => (
              <TableRow key={t._id}>
                <TableCell>{t.title || '—'}</TableCell>
                <TableCell>{personLabel(t.userId)}</TableCell>
                <TableCell>{formatDate(t.dueDate)}</TableCell>
                <TableCell>
                  <Chip size="small" label={t.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="Overtime" count={overtime.length}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {overtime.length === 0 && <EmptyRow colSpan={5} label="No overtime requests" />}
            {overtime.map((o) => (
              <TableRow key={o._id}>
                <TableCell>{formatDate(o.date)}</TableCell>
                <TableCell>{personLabel(o.userId)}</TableCell>
                <TableCell>{o.hours}</TableCell>
                <TableCell>
                  <Chip size="small" label={o.status} />
                </TableCell>
                <TableCell>{o.reason || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </Box>
  );
}

export default TodayThisWeekPanel;
